import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SeoKeywordOpportunity, OpportunityStatus, FixGuidance, AiSuggestion, SeoTask } from '@/types/api/seo';
import { PlaybookPhaseData } from './playbookConfig';
import PlaybookStep from './PlaybookStep';

type Props = {
  phase: PlaybookPhaseData;
  defaultExpanded?: boolean;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
  onGetFixGuidance: (opportunity: SeoKeywordOpportunity) => Promise<FixGuidance | null>;
  onGetSuggestions: (opportunity: SeoKeywordOpportunity) => Promise<AiSuggestion[] | null>;
  onApplyToWordPress: (opportunityId: string, suggestion: AiSuggestion, pageUrl: string | null) => Promise<{ success: boolean; error?: string }>;
  wpConnected: boolean;
  rankingPageUrl: string | null;
  currentMetrics: { position: number | null; ctr: number | null; clicks: number | null; impressions: number | null };
  onGenerateStepSuggestions: (stepId: string, stepLabel: string, stepDescription: string) => Promise<void>;
  onAddTask: (suggestion: AiSuggestion, opportunityId: string) => Promise<boolean>;
  existingTasks: SeoTask[];
};

const PlaybookPhase = function ({
  phase,
  defaultExpanded = false,
  onStatusChange,
  onGetFixGuidance,
  onGetSuggestions,
  onApplyToWordPress,
  wpConnected,
  rankingPageUrl,
  currentMetrics,
  onGenerateStepSuggestions,
  onAddTask,
  existingTasks,
}: Props): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isComplete = phase.completedSteps === phase.totalSteps && phase.stepsData.every((s) => s.status === 'completed');
  const progress = phase.totalSteps > 0 ? (phase.completedSteps / phase.totalSteps) * 100 : 0;

  // Find the first step that needs attention
  const firstIncompleteStepId = phase.stepsData.find((s) => s.status !== 'completed')?.id;

  return (
    <Box sx={{
      mb: 2,
      border: '1px solid',
      borderColor: expanded ? `${phase.color}30` : 'divider',
      borderRadius: 3,
      overflow: 'hidden',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s',
    }}>
      {/* Phase header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#fafafa' },
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Phase number badge */}
        <Box sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isComplete ? '#0D9D58' : phase.color,
          color: '#fff',
          fontWeight: 800,
          fontSize: 16,
          flexShrink: 0,
        }}>
          {isComplete ? '✓' : phase.number}
        </Box>

        {/* Phase info */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" component="div" fontWeight={700} sx={{ fontSize: 14 }}>
              {phase.label}
            </Typography>
            <Chip
              label={phase.timeline}
              size="small"
              sx={{ fontSize: 10, height: 18, backgroundColor: `${phase.color}15`, color: phase.color }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            {phase.subtitle}
          </Typography>
        </Box>

        {/* Progress */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={32}
              thickness={4}
              sx={{ color: isComplete ? '#0D9D58' : phase.color }}
            />
            <Box sx={{
              position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: 9 }}>
                {phase.completedSteps}/{phase.totalSteps}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Phase body */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 1.5 }}>
          {phase.stepsData.map((step, i) => (
            <PlaybookStep
              key={step.id}
              step={step}
              phaseColor={phase.color}
              isLast={i === phase.stepsData.length - 1}
              defaultExpanded={step.id === firstIncompleteStepId}
              onStatusChange={onStatusChange}
              onGetFixGuidance={onGetFixGuidance}
              onGetSuggestions={onGetSuggestions}
              onApplyToWordPress={onApplyToWordPress}
              wpConnected={wpConnected}
              rankingPageUrl={rankingPageUrl}
              currentMetrics={currentMetrics}
              onGenerateStepSuggestions={onGenerateStepSuggestions}
              onAddTask={onAddTask}
              existingTasks={existingTasks}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PlaybookPhase;
